// ProtectedRoute.tsx — gates the condo app behind a valid, unexpired JWT.
//
// Why this exists:
//   Previously every <Route path="/condo-app/*"> rendered <MainDashboard/>
//   directly. The dashboard tried to recover from "no user" internally, which
//   meant an unauthenticated visitor would briefly see the chrome (sidebar,
//   header) before being bounced. It also meant any code path that forgot to
//   call checkAuth() rendered protected UI to anonymous callers.
//
//   This wrapper makes the contract explicit: nothing inside renders unless
//   we have a session that the backend has not (yet) rejected.
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuthStore } from '../../store/authStore';
import { getAccessToken } from '../../services/httpClient';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Where to send unauthenticated users. Defaults to the condo login page. */
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = '/login-condo-app/',
}) => {
  const location = useLocation();
  const { user, isAuthenticated, checkAuth } = useAuthStore();

  // Sprint 110 (110.1) — the access token is in memory only and is EMPTY on a
  // fresh page load, so we can no longer gate synchronously on a localStorage
  // token. Instead: if zustand-persist rehydrated a logged-in session (user /
  // isAuthenticated) but the in-memory token is gone (reload), run checkAuth()
  // — which calls the silent bootstrap refresh against the HttpOnly cookie —
  // and show the loader until it settles. Only after the bootstrap fails do we
  // redirect to login. This is what makes "stay logged in across reload" work
  // without ever persisting a token to disk.
  const hadSession = isAuthenticated || !!user;
  const hasToken = !!getAccessToken();
  const [bootstrapped, setBootstrapped] = useState<boolean>(hasToken || !hadSession);

  useEffect(() => {
    let cancelled = false;
    if (!bootstrapped && !user) {
      // Re-hydrate (or fail) exactly once.
      Promise.resolve(checkAuth()).finally(() => {
        if (!cancelled) setBootstrapped(true);
      });
    }
    return () => { cancelled = true; };
  }, [bootstrapped, user, checkAuth]);

  // No prior session at all → straight to login, no loader.
  if (!hadSession && !hasToken) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  // Prior session but bootstrap finished and produced no user → redirect.
  if (bootstrapped && !user) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  // Only gate on `!user` here. We deliberately do NOT block on `isLoading`
  // because the auth store's `isLoading` flag is also flipped true by
  // background data refetches (fetchAvailableCondos, checkAuth, etc). If we
  // unmounted the dashboard every time one of those fired, the dashboard's
  // mount effect would re-fire the same fetch on remount → infinite loop
  // that kept the user stuck on "Verifying session..." forever. Once `user`
  // is populated the session is valid; loading states below that are the
  // dashboard's responsibility to display inline.
  if (!user) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 16,
      }}>
        {/* antd 6 only renders Spin's `tip` in nest/fullscreen patterns,
            not standalone. Render the label as a sibling instead so
            "Verifying session..." stays user-visible AND the test that
            asserts that exact text continues to find it. */}
        <Spin size="large" />
        <div>Verifying session...</div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
